import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  IconButton,
  Stack,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  Badge,
  Tooltip,
  Divider,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import FilterListIcon from '@mui/icons-material/FilterList';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import type { SoothMessage } from '../../types/sooth';

const PRIORITY_COLORS: Record<string, 'error' | 'warning' | 'info' | 'default'> = {
  high: 'error',
  medium: 'warning',
  low: 'info',
  none: 'default',
};

const ROUTING_COLORS: Record<string, 'success' | 'warning' | 'info' | 'default'> = {
  notified: 'success',
  queued: 'warning',
  summarised: 'info',
  silenced: 'default',
};

function timeAgo(dateStr: string): string {
  const now = new Date();
  const date = new Date(dateStr);
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

const SoothInboxPage: React.FC = () => {
  const { token } = useAuth();
  const [messages, setMessages] = useState<SoothMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [routingFilter, setRoutingFilter] = useState('');
  const [channelFilter, setChannelFilter] = useState('');
  const [unreadCount, setUnreadCount] = useState(0);
  const wsRef = useRef<WebSocket | null>(null);

  const fetchMessages = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (priorityFilter) params.append('priority', priorityFilter);
      if (routingFilter) params.append('routing', routingFilter);
      if (channelFilter) params.append('source_channel', channelFilter);
      const response = await axios.get(`/api/v1/sooth/messages/?${params.toString()}`);
      const data = response.data.results || response.data;
      setMessages(data);
      setUnreadCount(data.filter((m: SoothMessage) => !m.read_at).length);
      setError('');
    } catch (err: any) {
      setError('Failed to load messages');
    } finally {
      setLoading(false);
    }
  }, [priorityFilter, routingFilter, channelFilter]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  // WebSocket for real-time updates
  useEffect(() => {
    if (!token) return;

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${protocol}//${window.location.host}/ws/sooth/?token=${token}`;

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'sooth_message' || data.type === 'new_message') {
            setMessages((prev) => [data.message || data, ...prev]);
            setUnreadCount((prev) => prev + 1);
          }
        } catch (e) {
          // ignore parse errors
        }
      };

      ws.onerror = () => {
        console.log('Sooth WebSocket connection failed — real-time updates unavailable');
      };
    } catch (e) {
      // WebSocket not available
    }

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [token]);

  const markRead = async (messageId: number) => {
    try {
      await axios.post(`/api/v1/sooth/messages/${messageId}/mark_read/`);
      setMessages((prev) =>
        prev.map((m) =>
          m.id === messageId ? { ...m, read_at: new Date().toISOString() } : m
        )
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (err) {
      // silently fail
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="h4">Sooth Inbox</Typography>
          {unreadCount > 0 && (
            <Badge badgeContent={unreadCount} color="error">
              <Chip label="unread" size="small" variant="outlined" />
            </Badge>
          )}
        </Stack>
        <IconButton onClick={fetchMessages} disabled={loading}>
          <RefreshIcon />
        </IconButton>
      </Stack>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <FilterListIcon color="action" />
          <TextField
            select
            label="Priority"
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value)}
            size="small"
            sx={{ minWidth: 120 }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="high">High</MenuItem>
            <MenuItem value="medium">Medium</MenuItem>
            <MenuItem value="low">Low</MenuItem>
            <MenuItem value="none">None</MenuItem>
          </TextField>
          <TextField
            select
            label="Routing"
            value={routingFilter}
            onChange={(e) => setRoutingFilter(e.target.value)}
            size="small"
            sx={{ minWidth: 120 }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="notified">Notified</MenuItem>
            <MenuItem value="queued">Queued</MenuItem>
            <MenuItem value="summarised">Summarised</MenuItem>
            <MenuItem value="silenced">Silenced</MenuItem>
          </TextField>
          <TextField
            select
            label="Channel"
            value={channelFilter}
            onChange={(e) => setChannelFilter(e.target.value)}
            size="small"
            sx={{ minWidth: 140 }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="whatsapp_baileys">WhatsApp (Baileys)</MenuItem>
            <MenuItem value="whatsapp_cloud">WhatsApp (Cloud)</MenuItem>
          </TextField>
        </Stack>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : messages.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="textSecondary">No messages yet. Incoming WhatsApp messages will appear here.</Typography>
        </Paper>
      ) : (
        <Stack spacing={1}>
          {messages.map((msg) => (
            <Paper
              key={msg.id}
              sx={{
                p: 2,
                borderLeft: '4px solid',
                borderColor: msg.read_at
                  ? 'grey.300'
                  : msg.priority === 'high'
                  ? 'error.main'
                  : msg.priority === 'medium'
                  ? 'warning.main'
                  : 'primary.main',
                bgcolor: msg.read_at ? 'background.default' : 'action.hover',
                cursor: 'pointer',
                '&:hover': { bgcolor: 'action.selected' },
              }}
              onClick={() => !msg.read_at && markRead(msg.id)}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box sx={{ flex: 1, mr: 2 }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                    <Typography variant="subtitle2" fontWeight={msg.read_at ? 400 : 700}>
                      {msg.sender_display || msg.sender_id}
                    </Typography>
                    {msg.thread_id && (
                      <Chip label="Group" size="small" variant="outlined" sx={{ height: 20 }} />
                    )}
                  </Stack>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: msg.read_at ? 400 : 600,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: '600px',
                    }}
                  >
                    {msg.body || '[media]'}
                  </Typography>
                  {msg.matched_rule_name && (
                    <Typography variant="caption" color="textSecondary">
                      Rule: {msg.matched_rule_name}
                    </Typography>
                  )}
                </Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip label={msg.priority} size="small" color={PRIORITY_COLORS[msg.priority] || 'default'} />
                  <Chip label={msg.routing} size="small" color={ROUTING_COLORS[msg.routing] || 'default'} variant="outlined" />
                  <Typography variant="caption" color="textSecondary" sx={{ minWidth: 50, textAlign: 'right' }}>
                    {timeAgo(msg.message_timestamp || msg.created_at)}
                  </Typography>
                  {!msg.read_at && (
                    <Tooltip title="Mark as read">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          markRead(msg.id);
                        }}
                      >
                        <MarkEmailReadIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}
    </Box>
  );
};

export default SoothInboxPage;
